import { createBrowserRouter, Navigate } from "react-router-dom";
import App from "@/App";
import Page404 from "./pages/Page404";
import Login from "./pages/Login";
import RootLayout from "./components/layouts/RootLayout";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Invitation from "./pages/Invitation";
import ChooseOutlets from "./pages/ChooseOutlets";
import StandaloneAccountSettings from "./pages/StandaloneAccountSettings";
import Redirect from "./pages/Redirect";
import ChooseOrg from "./pages/ChooseOrg";
import ChooseModule from "./pages/ChooseModule";
import OrganizationUnavailable from "./components/OrganizationUnavailable";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { lazy, Suspense } from "react";
import AppSkeleton from "./components/AppSkeleton";
import { app_env } from "./env-config";
import TableTronicHome from "./pages/table-tronic/TableTronicHome";
import TbLogin from "./pages/table-tronic/TbLogin";
import TbForgotPassword from "./pages/table-tronic/TbForgotPassword";
import TbResetPassword from "./pages/table-tronic/TbResetPassword";
import TbInvitation from "./pages/table-tronic/TbInvitation";
import TbChooseOrg from "./pages/table-tronic/TbChooseOrg";
import TbRedirect from "./pages/table-tronic/TbRedirect";
import TbRootLayout from "./pages/table-tronic/TbRootLayout";

const SalesRoutes = lazy(() => import("./routes/sales"));
const RestaurantRoutes = lazy(() => import("./routes/restaurant"));
const InventoryRoutes = lazy(() => import("./routes/inventory"));
const ManufacturingRoutes = lazy(() => import("./routes/manufacturing"));
const HotelRoutes = lazy(() => import("./routes/hotel"));
const FinanceRoutes = lazy(() => import("./routes/finance"));
const CRMRoutes = lazy(() => import("./routes/crm"));
const FacilityRoutes = lazy(() => import("./routes/facility"));
const FacilityGuestDesk = lazy(() => import("./pages/FacilityGuestDesk"));
const RestaurantPosRoutes = lazy(() => import("./routes/restaurant-pos"));
const SettingsRoutes = lazy(() => import("./routes/settings"));
const AdminRoutes = lazy(() => import("./routes/admin"));
const BillingRequired = lazy(() => import("./pages/BillingRequired"));
const POS = lazy(() => import("./pages/POS"));
const Kds = lazy(() => import("./pages/Kds"));
const QRTableMenu = lazy(() => import("./pages/QRTableMenu"));
const PublicMenu = lazy(() => import("./pages/PublicMenu"));
const QRTableBill = lazy(() => import("./pages/QRTableBill"));
const PosQrPay = lazy(() => import("./pages/PosQrPay"));
const FacilityPortalRoutes = lazy(() => import("./routes/facility-portal"));
const withAppSuspense = (element: JSX.Element) => (
  <Suspense fallback={<AppSkeleton />}>{element}</Suspense>
);

const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      {
        element: <RootLayout />,
        children: [
          // `/` is the sign-in page. The public website (one-suite.cloud's
          // home, products, pricing...) is its own Next.js app in website/,
          // deployed separately. TableTronic keeps its own home page.
          {
            index: true,
            element:
              app_env === "table-tronic" ? (
                <TableTronicHome />
              ) : (
                <Navigate to="/login" replace />
              ),
          },
          {
            path: "/login",
            element: app_env === "table-tronic" ? <TbLogin /> : <Login />,
          },
          {
            path: "/forgot-password",
            element:
              app_env === "table-tronic" ? (
                <TbForgotPassword />
              ) : (
                <ForgotPassword />
              ),
          },
          {
            path: "/reset-password",
            element:
              app_env === "table-tronic" ? (
                <TbResetPassword />
              ) : (
                <ResetPassword />
              ),
          },
          {
            path: "/invite/:token",
            element:
              app_env === "table-tronic" ? <TbInvitation /> : <Invitation />,
          },
        ],
      },
      {
        element: (
          <Suspense fallback={<AppSkeleton />}>
            {app_env === "table-tronic" ? <TbRootLayout /> : <RootLayout />}
          </Suspense>
        ),
        children: [
          {
            path: "choose-org",
            element:
              app_env === "table-tronic" ? <TbChooseOrg /> : <ChooseOrg />,
          },
          { path: "choose-module", element: <ChooseModule /> },
          // Terminal screen for ORGANIZATION_INACTIVE responses. Lives outside
          // the org-scoped routes so it still renders while every org-scoped
          // API call is being rejected. Same component the domain-level
          // "suspended" screen uses, so there is one lock-out story.
          {
            path: "organization-inactive",
            element: <OrganizationUnavailable reason="inactive" />,
          },
          // Terminal screen for SUBSCRIPTION_LOCKED (402) responses. Outside
          // the org-scoped routes for the same reason as the screen above, and
          // additionally because it is the one screen a locked-out payer must
          // still be able to use — it is where they pay.
          {
            path: "billing-required",
            element: withAppSuspense(<BillingRequired />),
          },
          { path: "account-settings", element: <StandaloneAccountSettings /> },
          {
            path: "redirect",
            element: app_env === "table-tronic" ? <TbRedirect /> : <Redirect />,
          },
          {
            element: (
              <ProtectedRoute
                entity="access_pos"
                children={<ChooseOutlets />}
              />
            ),
            path: "sales/pos",
            handle: { crumb: () => "chooseOutlet" },
          },
          {
            element: (
              <ProtectedRoute
                entity="access_pos"
                children={withAppSuspense(<POS />)}
              />
            ),
            path: "sales/pos/:outletId",
            handle: { crumb: () => "pos" },
          },
          {
            path: "sales/*",
            element: withAppSuspense(<SalesRoutes />),
            handle: { crumb: () => "salesManagement" },
          },
          {
            element: withAppSuspense(<RestaurantPosRoutes />),
            path: "restaurant/pos/*",
          },
          // The kitchen display. A full-screen route like the POS rather than
          // a page inside the back office: it lives on a tablet above a pass,
          // where a sidebar and a breadcrumb are wasted pixels.
          {
            element: (
              <ProtectedRoute
                entity="can_access_kds"
                children={withAppSuspense(<Kds />)}
              />
            ),
            path: "restaurant/kds",
          },
          {
            path: "restaurant/*",
            element: withAppSuspense(<RestaurantRoutes />),
            handle: { crumb: () => "restaurantManagement" },
          },
          {
            path: "inventory/*",
            element: withAppSuspense(<InventoryRoutes />),
            handle: { crumb: () => "inventoryManagement" },
          },
          {
            path: "manufacturing/*",
            element: withAppSuspense(<ManufacturingRoutes />),
            handle: { crumb: () => "manufacturing" },
          },
          {
            element: withAppSuspense(<FinanceRoutes />),
            path: "financial-management/*",
            handle: { crumb: () => "financialManagement" },
          },
          {
            element: withAppSuspense(<CRMRoutes />),
            path: "crm/*",
            handle: { crumb: () => "crm" },
          },
          {
            element: withAppSuspense(<HotelRoutes />),
            path: "hotel/*",
            handle: { crumb: () => "hotelManagement" },
          },
          {
            element: (
              <ProtectedRoute
                entity="access_facility_guest_desk"
                children={withAppSuspense(<FacilityGuestDesk />)}
              />
            ),
            path: "facility/guest-desk",
            handle: { crumb: () => "guestDesk" },
          },
          {
            element: withAppSuspense(<FacilityRoutes />),
            path: "facility/*",
            handle: { crumb: () => "facilityManagement" },
          },
          {
            path: "settings/*",
            element: withAppSuspense(<SettingsRoutes />),
            handle: { crumb: () => "settings" },
          },
          {
            element: withAppSuspense(<AdminRoutes />),
            path: "admin/*",
            handle: { crumb: () => "admin" },
          },
        ],
      },
      // The facility member portal. A sibling of the staff app, not a child of
      // it: no RootLayout, no ProtectedRoute, its own session.
      {
        path: "/facility-customer-portal/*",
        element: withAppSuspense(<FacilityPortalRoutes />),
      },
      {
        path: "/menu",
        element: withAppSuspense(<QRTableMenu />),
      },
      {
        path: "/menu/preview/:restaurantDomain",
        element: withAppSuspense(<QRTableMenu />),
      },
      {
        path: "/t/:tableId",
        element: withAppSuspense(<QRTableMenu />),
      },
      // The read-only menu a restaurant links from its own site: the same
      // dishes as the QR menu, with no cart and no bill.
      {
        path: "/public-menu",
        element: withAppSuspense(<PublicMenu />),
      },
      {
        path: "/public-menu/:restaurantDomain",
        element: withAppSuspense(<PublicMenu />),
      },
      {
        path: "/b/:billId/pay",
        element: withAppSuspense(<QRTableBill />),
      },
      // What a customer's phone opens after scanning the code on a till's
      // screen. A sibling of the bill page above, and public for the same
      // reason: the person paying has no account here.
      {
        path: "/pay/:token",
        element: withAppSuspense(<PosQrPay />),
      },
      {
        path: "*",
        element: <Page404 />,
        handle: { crumb: () => "pageNotFound" },
      },
    ],
  },
]);

export default router;
